import { Money } from "@quint/components";
import { formatBigNumber } from "@quint/services/web3";
import {
  useConventionalPoolV1CalculateTokenReward,
  useConventionalPoolV1Claim,
  useConventionalPoolV1GetUserInfo,
  useConventionalPoolV1UserTokenStakeInfo,
  useConventionalPoolV1Withdraw,
  usePrepareConventionalPoolV1Claim,
  usePrepareConventionalPoolV1Withdraw,
} from "@quint/services/web3/hooks/conventional-pool-v1";
import { useAccount, useWaitForTransaction } from "wagmi";
import Staking from "../staking";
import { useToast } from "@quint/ui/toast";
import { useCallback } from "react";
import { parseError } from "@quint/services/web3/utils/parse-error";
import { BigNumber } from "ethers";
import {
  useConventionalPoolV2IsMigrated,
  useConventionalPoolV2Migrate,
  usePrepareConventionalPoolV2Migrate,
} from "@quint/services/web3/hooks/conventional-pool-v2";

export const QuintV1Rewards = () => {
  const { address } = useAccount();
  const { data, isLoading } = useConventionalPoolV1CalculateTokenReward({
    args: [address!],
    enabled: Boolean(address),
  });
  return <Money value={formatBigNumber(data)} isLoading={isLoading} />;
};

export const QuintV1CurrentlyStaked = () => {
  const { address } = useAccount();
  const { data, isLoading } = useConventionalPoolV1UserTokenStakeInfo({
    args: [address!],
    enabled: Boolean(address),
  });
  return <Money value={formatBigNumber(data?._amount)} isLoading={isLoading} />;
};

export const QuintV1TotalHarvested = () => {
  const { address } = useAccount();
  const { data, isLoading } = useConventionalPoolV1GetUserInfo({
    args: [address!],
    enabled: Boolean(address),
  });
  return <Money value={formatBigNumber(data?._totalWithdrawanToken)} isLoading={isLoading} />;
};

export const QuintV1HarvestButton = () => {
  const { enqueueToast } = useToast();
  const {
    config,
    error,
    isLoading: isPreparing,
  } = usePrepareConventionalPoolV1Claim({
    args: [BigNumber.from("0")],
  });

  const { write, data, isLoading: isWriting } = useConventionalPoolV1Claim(config);
  const onClick = useCallback(() => {
    if (error) {
      enqueueToast({
        variant: "error",
        title: "Failed To Harvest",
        message: parseError(error),
      });
      return;
    }
    if (!write) {
      enqueueToast({
        variant: "error",
        title: "Preparing...",
        message: "Please wait we are preparing your data",
      });
      return;
    }
    write?.();
  }, [enqueueToast, error, write]);

  const { isLoading: isWaiting } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      enqueueToast({
        variant: "success",
        title: "Harvest Success",
        message: "Successfully harvested your tokens",
      });
    },
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Harvest",
        message: parseError(err),
      });
    },
  });
  const loading = isPreparing || isWriting || isWaiting;
  return <Staking.HarvestButton deprecated loading={loading} onClick={onClick} />;
};

export const QuintV1UnstakeButton = () => {
  const { enqueueToast } = useToast();
  const { address } = useAccount();
  const isMigratedQuery = useConventionalPoolV2IsMigrated({
    args: [address!],
    enabled: Boolean(address),
  });
  const enabled = isMigratedQuery.data;
  const {
    config,
    error,
    isLoading: isPreparing,
  } = usePrepareConventionalPoolV1Withdraw({
    args: [BigNumber.from("0")],
    enabled,
  });

  const { write, data, isLoading: isWriting } = useConventionalPoolV1Withdraw(config);
  const onClick = useCallback(() => {
    if (error) {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: parseError(error),
      });
      return;
    }
    if (!write) {
      enqueueToast({
        variant: "error",
        title: "Preparing...",
        message: "Please wait we are preparing your data",
      });
      return;
    }
    write?.();
  }, [enqueueToast, error, write]);

  const { isLoading: isWaiting } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      enqueueToast({
        variant: "success",
        title: "Unstake Success",
        message: "Successfully harvested your tokens",
      });
    },
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: parseError(err),
      });
    },
  });
  const loading = isPreparing || isWriting || isWaiting || isMigratedQuery.isLoading;
  return (
    <Staking.UnstakeButton
      disabled={isMigratedQuery.isLoading || !isMigratedQuery.data}
      loading={loading}
      onClick={onClick}
    />
  );
};

export const QuintV1MigrateButton = () => {
  const { enqueueToast } = useToast();
  const { address } = useAccount();
  const isMigratedQuery = useConventionalPoolV2IsMigrated({
    args: [address!],
    enabled: Boolean(address),
  });
  const enabled = !isMigratedQuery.data;
  const {
    config,
    error,
    isLoading: isPreparing,
  } = usePrepareConventionalPoolV2Migrate({
    enabled,
  });

  const { write, data, isLoading: isWriting } = useConventionalPoolV2Migrate(config);
  const onClick = useCallback(() => {
    if (error) {
      enqueueToast({
        variant: "error",
        title: "Failed To Migrate",
        message: parseError(error),
      });
      return;
    }
    if (!write) {
      enqueueToast({
        variant: "error",
        title: "Preparing...",
        message: "Please wait we are preparing your data",
      });
      return;
    }
    write?.();
  }, [enqueueToast, error, write]);

  const { isLoading: isWaiting } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      enqueueToast({
        variant: "success",
        title: "Migrate Success",
        message: "Successfully harvested your tokens",
      });
    },
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Migrate",
        message: parseError(err),
      });
    },
  });
  const loading = isPreparing || isWriting || isWaiting;
  return <Staking.MigrateButton loading={loading} disabled={isMigratedQuery.data} onClick={onClick} />;
};
